import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OOT from "Constants/services/oot";

import {
  setLoader,
} from "Store/contentState/subscriptionSlice";

import { useSubscriptionAuth } from "./subscription/hooks/useSubscriptionAuth";
import Unauthorized from "./Unauthorized";
import { RootState } from "Store/mainStore";
import {
  setUserAccessInfo,
  setCountryList,
  setUserPersona
} from "Store/contentState/marketplaceSlice";
import Loader from "Components/Elements/Loader";
import { setLoggedInUserPermissions, setUserMetaData } from "Store/contentState/userManagementSlice";
import { setProp } from "Store/Actions";
import { setSupplierAcccountId } from "Store/contentState/merchantSlice";
import AppRoutes from "Routes/AppRoutes";
import { setLoggedInUserDetails } from "Store/contentState/commonSlice";
import { setDatamartUserDetails } from "dot/Components/Logics/ensoCommonFunctions";
import Downtime from "Components/Common/downtime/Downtime";
import { applyDealRegistrationNavEntitlement } from "Pages/dealRegistration/dealRegistrationAccess";

//Only for onboarding related imports below this line
import { prefillRegistrationForm } from 'Pages/Onboarding/Utils/prefillRegistrationForm';


// const Orders = React.lazy(() => import('./marketplace/orders/Orders'));
// const Quotations = React.lazy(() => import('./marketplace/quotations/Quotations'));
// const Admin = React.lazy(() => import('./admin/Admin'));


export const Landing = () => {
  // Check for maintenance mode
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  const { useAuthState } = useSubscriptionAuth();
  const { isAuthenticated } = useAuthState();
  const authSelector = useSelector((state: RootState) => state.authSlice);
  const loginUserId = useSelector(
    (state: RootState) => state.authSlice.login.id
  );

  const marketplaceSelector = useSelector(
    (state: RootState) => state.marketplace
  );

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false)



  const getUserInfo = async (loginUserId) => {
    // Handle default user role view
    let userRoleView = [];
    setLoading(true)
    try {
      // <Get configured user access
      let response = await OOT.getUserAccessControlInfo(loginUserId);
      if (response.status && response.data.length > 0) {

        //special handling for onboarding user to fetch the account information from user API (this can be enhanced in future.)
        const userRoles = response.data[0]['role']?.['roles'] || [];
        const onboardingRoles = ['new_merchant', 'new_partner', 'new_direct_customer', 'new_end_customer'];
        const hasOnboardingRole = userRoles.some(role => onboardingRoles.includes(role));

        // If user has onboarding roles, call again with isOnboarding flag
        if (hasOnboardingRole) {
          response = await OOT.getUserAccessControlInfo(loginUserId, true);

          // Pre-fill registration form (no data stored, just transformed and dispatched)
          if (response.status && response.data.length > 0) {
            prefillRegistrationForm(response.data[0], dispatch, authSelector.login.mail);
          }
        }





        // this is required as when we navigate to offer page isAuth should be true else again the user api is called
        await setDatamartUserDetails(response?.data?.[0], dispatch);
        dispatch(setProp({ slice: "preCiqNetwork", key: "cpm", value: response.data[0]['role']?.cpm }))
        dispatch(setUserPersona(response.data[0]['role']['persona'][0]))
        dispatch(setLoggedInUserDetails(response.data[0]['role']));
        dispatch(setProp({ slice: 'userManagement', key: 'loggedInUserRole', value: response.data[0]['role']['roles'] }))
        dispatch(setProp({ slice: 'userManagement', key: 'ootRoles', value: response.data[0]['role']?.['oot']?.['roles'] || [] }))
        dispatch(setLoggedInUserPermissions(
          {
            userPermissions: response.data[0]?.['metadata']?.['dataElements'] || []
          }
        ));


        // Add Ask Business Platform to metadata
        // response.data[0]["metadata"]["applicationListNameMapping"]["ask-business-platform"] = "Ask Business Platform";
        if (response.data[0]['role']['persona'].includes("merchant")) {
          dispatch(setSupplierAcccountId((response.data[0]['role']['supplier_account_id'])));
        }
        // else {
        // Handle user role view
        const userMetadata = response.data[0]["metadata"] || {};
        const applicationNameMapping = userMetadata["applicationListNameMapping"] || {};
        const applicationIdByName = Object.entries(applicationNameMapping).reduce((acc, [id, name]) => {
          acc[String(name).trim().toLowerCase()] = id;
          return acc;
        }, {} as Record<string, string>);
        const normalizeApplicationId = (application: string) => applicationIdByName[String(application).trim().toLowerCase()] || application;
        const applicationListEntry = userMetadata["applicationList"]?.[0] || {};
        const rawApplications = Array.isArray(applicationListEntry.groups)
          ? applicationListEntry.groups.flatMap((group: any) => group?.applications || [])
          : (applicationListEntry['applications'] || []);
        userRoleView = applyDealRegistrationNavEntitlement(
          rawApplications.map(normalizeApplicationId),
          userMetadata
        );
        if (userRoleView.includes("orders") || userRoleView.includes("merchant_orders")) {
          userRoleView.push("common_orders");
          response.data[0]["metadata"]["applicationListNameMapping"]["common_orders"] = "Orders";
        }

        //handle common products tab
        if (userRoleView.includes("nokia_products") || userRoleView.includes("products")) {
          userRoleView.push("common_products");
          response.data[0]["metadata"]["applicationListNameMapping"]["common_products"] = "Products";
        }

        //handle common contracts tab - only for Nokia users
        if (userRoleView.includes("frame_agreement") || userRoleView.includes("esign") || userRoleView.includes("agreement_template")) {
          // Check if user is from Nokia domain
          const isNokiaUser = response.data[0]['role']['persona'].includes("nokia")

          //if (isNokiaUser) {
          userRoleView.push("contract");
          response.data[0]["metadata"]["applicationListNameMapping"]["common_contracts"] = "Contract";
          //}
        }

        //user onboarding tabs
        if (userRoleView.includes("user_onboarding") || userRoleView.includes("onboarding_registration")) {
          userRoleView.push("onboarding");
          response.data[0]["metadata"]["applicationListNameMapping"]["onboarding"] = "User onboarding";
        }



        // userRoleView.push("merchantorder")
        const persona = response.data[0]['role']['persona'];
        //network tab restriction for certain personas for time being
        const restrictedPersonas = ["direct_reseller", "distributor", "direct_end_customer", "indirect_end_customer"];
        if (restrictedPersonas.includes(persona[0])) {
          const hasNetworks = userRoleView.some(app => app === "networks");
          if (hasNetworks) {
            userRoleView = userRoleView.filter(app => app !== "networks");
          }
        }
        if (userRoleView.includes("marketplace")) {
          getCountriesList();
          // let cartInfo =
          //   response.data[0]["cartInfo"].length > 0
          //     ? response.data[0]["cartInfo"][0]["cart"]
          //     : [];
          // dispatch(
          //   setProp({
          //     slice: "marketplace",
          //     key: "savedCartInfo",
          //     value: cartInfo,
          //   })
          // );
          // dispatch(setProp({slice:'marketplace', key: 'cart', value: cartInfo.cart_items}))
        }
        if (response.data[0]["metadata"]) {
          dispatch(setUserMetaData(response.data[0]["metadata"]));
        }

      }
      //}
    } catch (error) {
      console.error(
        "Error has occured on get user access control info :",
        error
      );
    } finally {
      setLoading(false)
      // Handle data format
      dispatch(setUserAccessInfo(userRoleView));
    }
  };

  useEffect(() => {
    if (loginUserId.length > 0) {
      getUserInfo(loginUserId);
      findUserType(authSelector.login.mail);
      //getCartInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginUserId]);

  const findUserType = (mail) => {
    // Extract the domain from the email address
    const domain = mail.split("@")[1];
    // Check if the domain is 'nokia.com'
    if (
      domain === "nokia.com" ||
      domain === "nokia-bell-labs.com" ||
      domain === "nokia-sbell.com"
    ) {
      dispatch(
        setProp({ slice: "marketplace", key: "isNokiaUser", value: true })
      );
    } else {
      dispatch(
        setProp({ slice: "marketplace", key: "isNokiaUser", value: false })
      );
    }
  };

  const getCountriesList = async () => {
    dispatch(
      setLoader({
        key: "isCountryLoading",
        value: true,
      })
    );
    try {
      let filterCondition = "is_allowed_for_marketplace = 1";
      const response = await OOT.getAllCountries(filterCondition);
      if (response.status) {
        const sortedData = response.data.filter(item => item.name && item.name.trim() !== "").sort((a, b) => a.name.localeCompare(b.name));
        const updatedData = sortedData.map((item) => {
          return {
            id: item.iso_2,
            value: item.name,
            is_merchant_products_allowed: item.is_merchant_products_allowed,
            short_region_code: item.short_region_code,
          };
        });
        dispatch(setCountryList(updatedData));
        dispatch(
          setLoader({
            key: "isCountryLoading",
            value: false,
          })
        );
      } else {
        dispatch(
          setLoader({
            key: "isCountryLoading",
            value: false,
          })
        );
      }
    } catch (error) {
      dispatch(
        setLoader({
          key: "isCountryLoading",
          value: false,
        })
      );
      console.error(error);
    }
  };

  if ((loading)) {
    return (
      <div style={{ position: "absolute", inset: "50% 0" }}>
        <Loader />
      </div>
    );
  }

  // Show downtime message if in maintenance mode
  if (isMaintenanceMode) {
    return <Downtime isAppDown={true} message="We're currently performing maintenance to improve your experience. We'll be back online shortly. Thank you for your patience." title="Scheduled Maintenance in Progress." />;
  }

  // If user is authenticated but not authorized, show unauthorized message
  if (isAuthenticated && marketplaceSelector.userAccessInfo.length === 0 && !loading) {
    return <Unauthorized />;
  }

  return (
    <>
      <AppRoutes
        isAuthenticated={isAuthenticated}
        userAccessInfo={marketplaceSelector.userAccessInfo}
      />
    </>
  );
};
